import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { useJourneyData } from "@/hooks/use-journey-data";
import type { Property } from "@/schemas/property";
import type { ViewingInvite } from "@/schemas/viewing-invite";
import { format } from "date-fns";
import { Copy, Mail, Pencil, Phone, User2 } from "lucide-react";
import { useEffect, useState } from "react";
import ContactButton from "./contact-button";

interface TimeSlot {
  date: Date;
  time: string;
}

interface ScheduleATourDialogProps {
  property: Property;
  invite: ViewingInvite | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
}

const TIME_SLOTS = [
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
];

function findTimeRanges(slots: TimeSlot[]): string[] {
  if (!slots.length) return [];

  // Group slots by date
  const slotsByDate = slots.reduce(
    (acc, slot) => {
      const dateKey = format(slot.date, "yyyy-MM-dd");
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(slot);
      return acc;
    },
    {} as Record<string, TimeSlot[]>,
  );

  return Object.entries(slotsByDate).map(([date, slots]) => {
    // Sort slots by time
    const sortedSlots = slots.sort((a, b) => a.time.localeCompare(b.time));

    // Find continuous ranges
    const ranges: { start: TimeSlot; end: TimeSlot }[] = [];
    let currentRange: { start: TimeSlot; end: TimeSlot } | null = null;

    sortedSlots.forEach((slot, index) => {
      const currentTime = new Date(`2000-01-01T${slot.time}`);
      const prevTime =
        index > 0
          ? new Date(`2000-01-01T${sortedSlots[index - 1].time}`)
          : null;

      if (!currentRange) {
        currentRange = { start: slot, end: slot };
      } else if (
        prevTime &&
        currentTime.getTime() - prevTime.getTime() === 30 * 60 * 1000
      ) {
        currentRange.end = slot;
      } else {
        ranges.push(currentRange);
        currentRange = { start: slot, end: slot };
      }
    });

    if (currentRange) {
      ranges.push(currentRange);
    }

    // Format the ranges for this date
    const dateStr = format(sortedSlots[0].date, "EEEE, MMMM d");
    const timeRanges = ranges
      .map((range) => {
        const startTime = format(
          new Date(`2000-01-01T${range.start.time}`),
          "h:mm a",
        );
        const endTime = format(
          new Date(`2000-01-01T${range.end.time}`),
          "h:mm a",
        );
        return range.start === range.end
          ? startTime
          : `${startTime} - ${endTime}`;
      })
      .join(" and ");

    return `- ${dateStr} at ${timeRanges}`;
  });
}

export function ScheduleATourDialog({
  property,
  invite,
  open,
  onOpenChange,
  onClose,
}: ScheduleATourDialogProps) {
  const [message, setMessage] = useState<string>("");
  const { journeyData, updateJourney } = useJourneyData();
  const [calLinkInput, setCalLinkInput] = useState("");
  const [isEditingCalLink, setIsEditingCalLink] = useState(false);
  const [contactMethod, setContactMethod] = useState<"email" | "phone">(
    property.listing_agent_email ? "email" : "phone",
  );

  const handleCalLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleanedInput = e.target.value.replace("https://cal.com/", "");
    setCalLinkInput(cleanedInput);
  };

  const handleSaveCalLink = () => {
    updateJourney.mutate(
      { calLink: calLinkInput },
      {
        onSuccess: () => {
          setIsEditingCalLink(false);
          toast({
            title: "Calendar Link Updated",
            description:
              "Remember to keep your calendar up to date so agents can see your real availability.",
          });
        },
      },
    );
  };

  useEffect(() => {
    if (isEditingCalLink) {
      setCalLinkInput(
        (journeyData?.calLink || "").replace("https://cal.com/", ""),
      );
    }
  }, [isEditingCalLink, journeyData?.calLink]);

  const getDefaultMessage = () =>
    invite?.email_body ||
    invite?.sms_body ||
    `Hi${property.listing_agent_name ? ` ${property.listing_agent_name}` : ""},

I'm interested in viewing ${property.address}. I am currently self-representing as a buyer. I do have a pre-approval letter from my lender and will provide it to you upon request. Please let me know if you have any questions.

Looking forward to hearing from you.

Thank you!`;

  useEffect(() => {
    setMessage(getDefaultMessage());
  }, [property.listing_agent_name, property.address, invite]);

  const handleCopyLink = () => {
    if (!invite?.short_link) return;
    navigator.clipboard.writeText(invite.short_link);
    toast({
      description: "Link copied to clipboard",
    });
  };

  const getPhoneLink = () => {
    if (!property.listing_agent_phone) return "#";
    const cleanPhone = property.listing_agent_phone.replace(/\D/g, "");
    return `tel:${cleanPhone}`;
  };

  const getSmsLink = () => {
    if (!property.listing_agent_phone) return "#";
    const cleanPhone = property.listing_agent_phone.replace(/\D/g, "");
    const encodedMessage = encodeURIComponent(message);
    return `sms:${cleanPhone}?body=${encodedMessage}`;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
        onOpenChange(isOpen);
      }}
    >
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            Schedule a Tour
            <Badge variant="secondary" className="ml-2">
              {property.address}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-4 px-6 py-4">
            {/* Agent Info Card */}
            <Card className="bg-muted/50">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User2 className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium">{property.listing_agent_name}</h3>
                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                    {property.listing_agent_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {property.listing_agent_phone}
                      </span>
                    )}
                    {property.listing_agent_email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {property.listing_agent_email}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Calendar Setup */}
            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">
                  Your Calendar Link
                </label>
                <div className="flex items-center border rounded-md overflow-hidden">
                  <span className="px-3 py-2 bg-muted text-muted-foreground text-sm border-r">
                    https://cal.com/
                  </span>
                  {isEditingCalLink ? (
                    <>
                      <Input
                        placeholder="your-username"
                        value={calLinkInput}
                        onChange={handleCalLinkChange}
                        className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 flex-1 text-sm"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-3"
                        onClick={handleSaveCalLink}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="px-3 py-2 flex-1 text-sm">
                        {journeyData?.calLink?.replace(
                          "https://cal.com/",
                          "",
                        ) || "Not set"}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-3"
                        onClick={() => setIsEditingCalLink(true)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Connect your calendar to automatically sync your availability.
                  Remember to keep it up to date!
                </p>
              </div>
            </div>

            {/* Message Section */}
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a message to the listing agent..."
              className="min-h-[120px]"
            />

            {/* Action Buttons */}
            <div className="border-t pt-3 flex flex-col gap-3">
              {invite?.short_link && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Share this link with the agent to let them book a time that
                    works with your calendar:
                  </p>
                  <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                    <code className="text-xs flex-1">{invite.short_link}</code>
                    <Button variant="ghost" size="sm" onClick={handleCopyLink}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              {property.listing_agent_email && property.listing_agent_phone ? (
                <div className="space-y-3">
                  <div className="flex flex-row gap-2">
                    <ContactButton
                      property={property}
                      disabled={!message || !journeyData?.calLink}
                      message={message}
                      type="email"
                    />
                  </div>
                  <div className="flex flex-row gap-2">
                    <ContactButton
                      property={property}
                      disabled={!message || !journeyData?.calLink}
                      message={message}
                      type="message"
                    />
                    <ContactButton
                      property={property}
                      disabled={!message || !journeyData?.calLink}
                      message={message}
                      type="call"
                    />
                  </div>
                </div>
              ) : property.listing_agent_email ? (
                <div className="space-y-3">
                  <ContactButton
                    property={property}
                    disabled={!message || !journeyData?.calLink}
                    message={message}
                    type="email"
                  />
                </div>
              ) : property.listing_agent_phone ? (
                <div className="space-y-3">
                  <div className="flex flex-row gap-2">
                    <ContactButton
                      property={property}
                      disabled={!message || !journeyData?.calLink}
                      message={message}
                      type="message"
                    />
                    <ContactButton
                      property={property}
                      disabled={!message || !journeyData?.calLink}
                      message={message}
                      type="call"
                    />
                  </div>
                </div>
              ) : (
                <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
                  <p className="text-center">
                    No contact information available
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
